// This should be the only place where svelte-router-spa/navigateTo can be imported
/* eslint-disable-next-line no-restricted-imports */
import { navigateTo as routerNavigateTo } from "svelte-router-spa";
import { pageMap } from "./stepMap";
import { NavigationType, getPath as getRoutePath, sessionStore, updateSession } from "../stores/session";
import type { FoojiData } from "../../types/campaign";
import Page404 from "../../components/Steps/404.svelte";
import { type Route as SerializedRoute } from "../stores/session";
import { campaignStore } from "../stores/campaign";
import { get } from "svelte/store";
import { type Campaign } from "../../types/campaign";
import { type ComponentType } from "svelte";
import PATHS from "../constants/routePaths";
import { isDemoMode } from "../stores/mode";
import { settingsStore, type SettingsStore } from "../stores/settings";
import { isUserBrowserCompatible } from "../core";
import { CampaignStatus, checkStatus } from "../public/lib/api/campaign";

type AppRoute = {
  name: string;
  redirectTo?: string;
  component?: ComponentType;
};

export const errorRoutes: Array<SerializedRoute> = [
  {
    id: "campaignOver",
    path: PATHS.CAMPAIGN_OVER,
    requiresAuth: false,
    public: false
  },
  {
    id: "cancelled",
    path: PATHS.CANCELLED,
    requiresAuth: false,
    public: false
  },
  {
    id: "error",
    path: PATHS.ERROR,
    requiresAuth: false,
    public: false
  },
  {
    id: "noOojiData",
    path: PATHS.NO_OOJI_DATA,
    requiresAuth: false,
    public: false
  },
  {
    id: "stockOut",
    path: PATHS.STOCK_OUT,
    requiresAuth: false,
    public: false
  },
  {
    id: "alreadyOrdered",
    path: PATHS.ALREADY_ORDERED,
    requiresAuth: false,
    public: false
  },
  {
    id: "tooMuchDemand",
    path: PATHS.TOO_MUCH_DEMAND,
    requiresAuth: false,
    public: false
  },
  {
    id: "rateLimit",
    path: PATHS.RATE_LIMIT,
    requiresAuth: false,
    public: false
  },
  {
    id: "rateLimitWithCountdown",
    path: PATHS.RATE_LIMIT_WITH_COUNTDOWN,
    requiresAuth: false,
    public: false
  },
  {
    id: "unlucky",
    path: PATHS.UNLUCKY,
    requiresAuth: false,
    public: false
  },
  {
    id: "linkUsed",
    path: PATHS.LINK_USED,
    requiresAuth: false,
    public: false
  },
  {
    id: "linkExpired",
    path: PATHS.LINK_EXPIRED,
    requiresAuth: false,
    public: false
  },
  {
    id: "botDetected",
    path: PATHS.BOT_DETECTED,
    requiresAuth: false,
    public: false
  },
  {
    id: "unsupportedBrowser",
    path: PATHS.UNSUPPORTED_BROWSER,
    requiresAuth: false,
    public: false
  },
  {
    id: "restrictedCampaignInfo",
    path: PATHS.RESTRICTED_CAMPAIGN_INFO,
    requiresAuth: false,
    public: false
  },
  {
    id: "tooYoung",
    path: PATHS.TOO_YOUNG,
    requiresAuth: false,
    public: false
  },
  {
    id: "authenticationError",
    path: PATHS.AUTHENTICATION_ERROR,
    requiresAuth: false,
    public: false
  },
  {
    id: "invalidChannel",
    path: PATHS.INVALID_CHANNEL,
    requiresAuth: false,
    public: false
  },
  {
    id: "emailVerified",
    path: PATHS.EMAIL_VERIFIED,
    requiresAuth: false,
    public: true // Needs to be true, it will be redirected from the auth-api, won't be on the allowlist
  },
  {
    id: "emailVerificationFailed",
    path: PATHS.EMAIL_VERIFICATION_FAILED,
    requiresAuth: false,
    public: true // Needs to be true, it will be redirected from the auth-api, won't be on the allowlist
  }
]
  .map((route) => ({ ...route, component: "GenericErrorPage" }))
  .concat({
    id: "noSupport",
    path: PATHS.NO_SUPPORT,
    requiresAuth: false,
    public: false,
    component: "NoSupport"
  })
  .concat({
    id: "invalidEntry",
    path: PATHS.INVALID_ENTRY,
    requiresAuth: false,
    public: true,
    component: "InvalidEntry"
  });

export const errorRoutePaths = errorRoutes.map(({ path }) => path);

export const createSystemRoutes = (settings: SettingsStore): SerializedRoute[] => {
  const isShowDeliveryZones: boolean = (settings.showDeliveryZones && !!settings.deliveryZones) ?? false;
  return [
    {
      id: "campaignTeaser",
      path: PATHS.CAMPAIGN_TEASER,
      requiresAuth: false,
      public: false,
      component: "CampaignTeaser"
    },
    {
      id: "success",
      path: PATHS.SUCCESS,
      requiresAuth: false,
      public: false,
      component: "Success"
    },
    {
      id: "rules",
      path: PATHS.RULES,
      requiresAuth: false,
      public: true,
      component: "Rules"
    },
    {
      id: "followUpSurvey",
      path: PATHS.FOLLOW_UP_SURVEY,
      requiresAuth: true,
      public: true,
      component: "Survey"
    },
    {
      id: "info",
      path: PATHS.INFO,
      requiresAuth: false,
      public: true,
      component: "Info"
    },
    {
      id: "extraInfo",
      path: PATHS.EXTRA_INFO,
      requiresAuth: false,
      public: true,
      component: "Info"
    },
    {
      id: "deliveryZones",
      path: PATHS.DELIVERY_ZONES,
      requiresAuth: false,
      public: isShowDeliveryZones,
      component: "DeliveryZones"
    },
    ...errorRoutes
  ];
};

export function hasInvalidChannel(channels: string[] = [], foojiData: FoojiData | undefined): boolean {
  if (isDemoMode()) {
    return false;
  }

  const campaignUserChannel = foojiData?.campaign_user?.channel;

  if (channels.length === 1 && channels[0] === "callofduty") {
    return false;
  }

  if (!campaignUserChannel && !channels.includes("anonymous")) {
    return true;
  }

  if (campaignUserChannel && !channels.includes(campaignUserChannel)) {
    return true;
  }

  return false;
}

const ALLOWED_PATHS_AFTER_CAMPAIGN = [PATHS.RULES, PATHS.CAMPAIGN_OVER];
const ALLOWED_PATHS_BEFORE_CAMPAIGN = [PATHS.RULES, PATHS.CAMPAIGN_TEASER];

/**
 * If we don't manage to load the microsite settings, campaign will be empty
 * hence the null check and making sure we navigate to the error page
 */
export function validateCampaign(): boolean {
  const campaign = get(campaignStore).campaign as Campaign;
  const path = document.location.pathname;

  if (!isUserBrowserCompatible()) {
    path !== PATHS.UNSUPPORTED_BROWSER && navigateToPath(PATHS.UNSUPPORTED_BROWSER);
    return false;
  }

  if (path === PATHS.FOLLOW_UP_SURVEY) {
    return true;
  }

  if (!campaign) {
    path !== PATHS.ERROR && navigateToPath(PATHS.ERROR);
    return false;
  }

  const status = checkStatus(campaign);
  if (status === CampaignStatus.TEASING) {
    ALLOWED_PATHS_BEFORE_CAMPAIGN.includes(path) || navigateToPath(PATHS.CAMPAIGN_TEASER);
  } else if (status === CampaignStatus.OVER) {
    ALLOWED_PATHS_AFTER_CAMPAIGN.includes(path) || navigateToPath(PATHS.CAMPAIGN_OVER);
  }

  return status === CampaignStatus.RUNNING;
}

export const buildRoutes = (): AppRoute[] => {
  const appRoutes = get(sessionStore)?.routes || [];
  const settings = get(settingsStore);
  const systemRoutes = createSystemRoutes(settings);
  const routes: AppRoute[] = [...appRoutes, ...systemRoutes].map(({ path, component }): AppRoute => {
    const safeComponent = component && component in pageMap ? pageMap[component as keyof typeof pageMap] : Page404;
    return { name: path, component: safeComponent };
  });

  const hasHomeRoute = routes.find((route: AppRoute) => route.name === "/");

  if (!hasHomeRoute && routes.length > 0) {
    routes.push({ name: "/", redirectTo: routes[0].name });
  }

  routes.push({ name: "404", component: Page404 });

  return routes;
};

const updateAllowList = (path: string) => {
  const session = get(sessionStore);
  const stepAllowList = [...(session?.stepAllowList ?? [])];
  if (!stepAllowList.includes(path)) {
    stepAllowList.push(path);
    updateSession({ stepAllowList });
  }
};

export const navigateTo = (direction: NavigationType = NavigationType.Next) => {
  const path = getRoutePath(direction);
  navigateToPath(path);
};

export const navigateToPath = (path: string) => {
  updateAllowList(path);
  routerNavigateTo(path);
};

export const clearAllowListExceptTheCurrentRoute = (currentRoute: string) => {
  const allowList = [currentRoute];
  updateSession({ stepAllowList: allowList });
};
